import React, { useState, useRef, useMemo, useEffect } from 'react';
import { styled, CommonStyles, Input, useClickOutside, SubTitle, Shape, Body } from '@teko/ds-foundation';
import { DeleteCircleFilled, ArrowDownFilled, CheckOutlined } from '@teko/ds-icon';
import { t } from '../../i18n.es.js';
import useEventCallback from 'use-event-callback';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Item = styled.div((props) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  return `
    height: 2.5rem;
    display: flex;
    box-sizing: border-box;
    width: 100%;
    position: relative;
    align-items: center;
    background: ${(_a = props.theme) == null ? void 0 : _a.colors.white};
    padding-left: ${(_b = props.theme) == null ? void 0 : _b.spacing.s16};
    padding-right: ${(_c = props.theme) == null ? void 0 : _c.spacing.s16};
    border-radius: ${(_d = props.theme) == null ? void 0 : _d.borderRadius.s};
    cursor: pointer;
    &:hover,
    &.focused {
      background: ${(_e = props.theme) == null ? void 0 : _e.colors.background};
    }
    ${props.hasDivider ? ` &:after {
         position: absolute;
         box-sizing: border-box;
         content: ' ';
         pointer-events: none;
         right: ${(_f = props.theme) == null ? void 0 : _f.spacing.s16};
         bottom: 0;
         left: ${(_g = props.theme) == null ? void 0 : _g.spacing.s16};
         border-bottom: ${(_h = props.theme) == null ? void 0 : _h.borderWeight.m} solid ${(_i = props.theme) == null ? void 0 : _i.colors.divider};
         -webkit-transform: scaleY(0.5);
         transform: scaleY(0.5);
       }` : ``}
`;
});
const LeftContent = styled.div`
  flex: 1;
  text-align: left;
`;
const RightContent = styled.div`
  width: 1.5rem;
  text-align: right;
`;
const optionHeight = 2.5;
const ItemsWrapper = styled.div(({ theme, maxPerView }) => `
    background: ${theme == null ? void 0 : theme.colors.white};
		box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
    border-radius: ${theme == null ? void 0 : theme.borderRadius.m};
    overflow: hidden;
    position: absolute;
    top: 2.75rem;
    width: 100%;
    z-index: 1000;
    ${maxPerView ? `height: ${maxPerView * optionHeight}rem; 
          overflow-y: scroll;
          scrollbar-gutter: stable;
          ::-webkit-scrollbar {
            width: 8px;
          }
          ::-webkit-scrollbar-thumb {
            background: #888; 
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: #555; 
          }` : ""}
`);
const SelectWrapper = styled.div`
  position: relative;
`;
const Wrapper = styled.div`
  ${CommonStyles}
`;
const Field = styled.div((props) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  return `
  background: ${(_a = props.theme) == null ? void 0 : _a.colors.white};
  height: ${optionHeight}rem;
  border-radius: ${(_b = props.theme) == null ? void 0 : _b.spacing.s4};
  display: flex;
  align-items: center;
  border: ${(_c = props.theme) == null ? void 0 : _c.borderWeight.m} solid ${(_d = props.theme) == null ? void 0 : _d.colors.divider};
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  ${props.disabled ? `
    cursor: not-allowed;
    background: ${(_e = props.theme) == null ? void 0 : _e.colors.background};
  ` : ""}
  ${!props.active ? `&:hover {
    border: ${(_f = props.theme) == null ? void 0 : _f.borderWeight.m} solid ${(_g = props.theme) == null ? void 0 : _g.colors.placeholder};` : ""}
  }
  ${props.active ? `
    border: ${(_h = props.theme) == null ? void 0 : _h.borderWeight.m} solid ${props.activeColor || ((_i = props.theme) == null ? void 0 : _i.colors.link500)};
  ` : ""}
`;
});
const Backdrop = styled.div(({ active }) => `
    position: absolute;
    animation-duration: 0.2s;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    visibility: hidden;
    ${active ? `
      visibility: visible;
      z-index: 990;
    ` : ""}
    transition: transform 0.3s;
    background: transparent;
`);
const InputSearch = styled(Input)`
  width: 100%;
  .input-container {
    border-style: none !important;
  }
`;
const findOptionByValue = (options, value) => {
  const index = options.findIndex((option) => option.value === value);
  return { option: options[index], index };
};
const scrollToFocusedOption = (list, focusedOption) => {
  if (list && focusedOption) {
    const optionTop = focusedOption.offsetTop;
    const optionBottom = optionTop + focusedOption.clientHeight;
    const listHeight = list.clientHeight;
    const listTop = list.scrollTop;
    const listBottom = listTop + listHeight;
    if (optionBottom > listBottom) {
      list.scrollTop = optionBottom - listHeight;
    } else if (optionTop < listTop) {
      list.scrollTop = optionTop;
    }
    focusedOption.classList.add("focused");
  }
};
const DropdownControl = (_a) => {
  var _b = _a, {
    selectedIndex,
    show,
    options,
    children,
    onClickOutside,
    onSelectOption,
    getDropdownContainer
  } = _b, restProps = __objRest(_b, [
    "selectedIndex",
    "show",
    "options",
    "children",
    "onClickOutside",
    "onSelectOption",
    "getDropdownContainer"
  ]);
  const [focusedIndex, setFocusedIndex] = useState(selectedIndex);
  const lastFocusedItem = useRef(null);
  useEffect(() => {
    setFocusedIndex(selectedIndex);
  }, [show, selectedIndex, setFocusedIndex]);
  const focusSelectedOption = useEventCallback(() => {
    var _a2;
    const container = getDropdownContainer();
    if (!container)
      return;
    const focusedItem = container.children[focusedIndex];
    (_a2 = lastFocusedItem.current) == null ? void 0 : _a2.classList.remove("focused");
    scrollToFocusedOption(container, focusedItem);
    lastFocusedItem.current = focusedItem;
  });
  const onKeyDown = (e) => {
    const { key } = e;
    switch (key) {
      case "Enter": {
        onSelectOption(options[focusedIndex]);
        break;
      }
      case "ArrowUp":
      case "ArrowDown": {
        e.preventDefault();
        setFocusedIndex((current) => {
          if (key === "ArrowUp") {
            return current <= 0 ? options.length - 1 : current - 1;
          } else if (key === "ArrowDown") {
            return current >= options.length - 1 ? 0 : current + 1;
          }
          return current;
        });
        break;
      }
      case "Tab":
      case "Escape": {
        onClickOutside();
        break;
      }
    }
  };
  useEffect(() => {
    if (show && focusedIndex > -1) {
      focusSelectedOption();
    }
  }, [show, focusedIndex, focusSelectedOption]);
  return /* @__PURE__ */ React.createElement(SelectWrapper, __spreadValues({
    onKeyDown
  }, restProps), children);
};
const Select = (_c) => {
  var _d = _c, {
    value,
    title,
    options = [],
    maxPerView,
    disabled = false,
    hasDivider = false,
    activeColor,
    selectValue,
    className,
    titleProps = {},
    placeholder,
    onDelete,
    allowDelete = true
  } = _d, rest = __objRest(_d, [
    "value",
    "title",
    "options",
    "maxPerView",
    "disabled",
    "hasDivider",
    "activeColor",
    "selectValue",
    "className",
    "titleProps",
    "placeholder",
    "onDelete",
    "allowDelete"
  ]);
  const [show, setShow] = useState(false);
  const optionRef = useRef(null);
  const [activeOption, setActiveOption] = useState();
  const [onHover, setOnHover] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [keyword, setKeyword] = useState("");
  const selectedOption = useMemo(() => findOptionByValue(options, value), [value, options]);
  const isDisabled = disabled || !options.length;
  useEffect(() => {
    const { option } = selectedOption;
    setActiveOption(option);
  }, [selectedOption]);
  useEffect(() => {
    setFilteredOptions(options);
  }, [JSON.stringify(options)]);
  useEffect(() => {
    let filteredOptions2 = [];
    if (keyword.length)
      filteredOptions2 = options.filter((item) => item.text.toLowerCase().indexOf(keyword.toLowerCase()) >= 0);
    else
      filteredOptions2 = options;
    setFilteredOptions(filteredOptions2);
  }, [keyword]);
  const onClickItem = (option) => {
    selectValue(option == null ? void 0 : option.value);
    setKeyword("");
    setShow(false);
  };
  const onClickOutside = useEventCallback(() => {
    setShow(false);
    if (!(activeOption == null ? void 0 : activeOption.value)) {
      setKeyword("");
      setActiveOption(void 0);
      if (value)
        selectValue("");
    }
  });
  useClickOutside(optionRef, onClickOutside);
  return /* @__PURE__ */ React.createElement(Wrapper, __spreadProps(__spreadValues({}, rest), {
    onKeyDown: (e) => {
      if (show) {
        e.stopPropagation();
      }
    },
    tabIndex: -1
  }), title && /* @__PURE__ */ React.createElement(SubTitle, __spreadValues({
    level: 4,
    line: 1,
    marginBottom: "s4"
  }, titleProps), title), /* @__PURE__ */ React.createElement(DropdownControl, {
    selectedIndex: selectedOption.index,
    show,
    options: filteredOptions,
    onClickOutside,
    getDropdownContainer: () => optionRef.current,
    onSelectOption: onClickItem,
    className
  }, /* @__PURE__ */ React.createElement(Field, {
    disabled: isDisabled,
    activeColor: activeColor || "",
    onClick: (e) => {
      e.stopPropagation();
      if (!disabled)
        setShow(!show);
    },
    onFocus: () => {
      if (!disabled) {
        setShow(true);
      }
    },
    active: show,
    onMouseEnter: () => setOnHover(true),
    onMouseLeave: () => setOnHover(false)
  }, /* @__PURE__ */ React.createElement(InputSearch, {
    disabled: isDisabled,
    placeholder: placeholder || t("Ch\u1ECDn"),
    onChange: (keyword2) => {
      setKeyword(keyword2);
      setActiveOption(void 0);
      setShow(true);
    },
    suffix: allowDelete && ((activeOption == null ? void 0 : activeOption.text) && onHover || keyword) && !isDisabled ? /* @__PURE__ */ React.createElement(DeleteCircleFilled, {
      color: "textSecondary",
      size: 16,
      onClick: (e) => {
        e.stopPropagation();
        setKeyword("");
        setActiveOption(void 0);
        selectValue("");
        onDelete == null ? void 0 : onDelete();
      }
    }) : /* @__PURE__ */ React.createElement(ArrowDownFilled, {
      color: "textSecondary",
      size: 20
    }),
    value: (activeOption == null ? void 0 : activeOption.text) || keyword
  })), show && /* @__PURE__ */ React.createElement(ItemsWrapper, {
    ref: optionRef,
    maxPerView: (maxPerView != null ? maxPerView : Number.MAX_SAFE_INTEGER) < filteredOptions.length ? maxPerView : void 0
  }, filteredOptions.length ? filteredOptions.map((option, index) => {
    var _a, _b, _c2, _d2;
    return /* @__PURE__ */ React.createElement(Item, {
      key: index,
      onClick: () => onClickItem(option),
      hasDivider: index === options.length - 1 ? false : hasDivider
    }, option.icon && /* @__PURE__ */ React.createElement(Shape, {
      marginRight: "s12"
    }, React.cloneElement(option.icon, {
      size: !((_b = (_a = option.icon) == null ? void 0 : _a.props) == null ? void 0 : _b.size) ? 24 : (_d2 = (_c2 = option.icon) == null ? void 0 : _c2.props) == null ? void 0 : _d2.size
    })), /* @__PURE__ */ React.createElement(LeftContent, null, /* @__PURE__ */ React.createElement(Body, {
      level: 1,
      line: 1
    }, option.text)), value === option.value && /* @__PURE__ */ React.createElement(RightContent, null, /* @__PURE__ */ React.createElement(CheckOutlined, {
      size: 24,
      color: activeColor || "primary500"
    })));
  }) : /* @__PURE__ */ React.createElement(Item, {
    hasDivider: false
  }, /* @__PURE__ */ React.createElement(LeftContent, null, /* @__PURE__ */ React.createElement(Body, {
    level: 1,
    line: 1,
    color: "textSecondary"
  }, t("NoData")))))), /* @__PURE__ */ React.createElement(Backdrop, {
    onClick: () => setShow(false),
    active: show
  }));
};

export { Select };
